import { DeliveryPackagesDataGrid } from "src/components/views/erp/delivery/deliveryPackage/DeliveryPackagesDataGrid.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";
import { useRefreshRef } from "src/utils/useRefreshRef.ts";
import { DeliveryPackageApi } from "src/api/generated/erp/delivery/api/deliveryPackageApi.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { DeliveryPackageToBundleSelectionView } from "src/components/views/erp/delivery/deliveryPackageBundle/DeliveryPackageToBundleSelectionView.tsx";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { getDeliveryPackageAddress } from "src/components/views/erp/delivery/deliveryPackageSourceUtils.ts";
import { DeliveryPackageWithSourcesView } from "src/api/generated/erp/db/types/tables/deliveryPackageWithSourcesView.ts";
import { AavoMenuDivider } from "src/components/common/menu/AavoMenuDivider.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DeliveryPackageBundleContentViewProps {
	packageBundle: DeliveryPackageWithSourcesView;
	refreshParent?: () => Promise<unknown>;
}

export const DeliveryPackageBundleContentView = ({
	packageBundle,
	refreshParent,
}: DeliveryPackageBundleContentViewProps) => {
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();
	const editDeliveryPackagesPermission = hasPermission(Permissions.Delivery.DeliveryPackages.MANAGE);

	const refreshRef = useRefreshRef();
	const deliveryAddress = getDeliveryPackageAddress(packageBundle);

	return (
		<DeliveryPackagesDataGrid
			refreshRef={refreshRef}
			gridId={"AA5CE965D0EA709B"}
			bundleHandling={"ONLY_NON_BUNDLES"}
			parentPackageBundleId={packageBundle.deliveryPackageId}
			showSiteField={false}
			actionBarExtraComponents={
				<>
					{editDeliveryPackagesPermission && packageBundle.deliveryId == null && (
						<AsyncButton
							label={i18n.t("add_packages")}
							icon={faPlus}
							variant="outlined"
							onClick={async () => {
								openDialog(({ closeDialog }) => ({
									title: i18n.t("delivery.add_packages_to_bundle"),
									size: "sm",
									fullScreenOnMobile: true,
									agentContext: {
										key: defineAgentUiContextKey("erp.delivery.addPackagesToBundle.dialog"),
										title: i18n.t("delivery.add_packages_to_bundle"),
										data: {
											deliveryPackageBundleId: packageBundle.deliveryPackageId,
										},
									},
									content: (
										<DeliveryPackageToBundleSelectionView
											initialDeliveryAddress={deliveryAddress}
											onCompleted={async ({ selectedPackages }) => {
												await DeliveryPackageApi.movePackagesToBundle({
													deliveryPackageIds: selectedPackages.map(
														(p) => p.deliveryPackageId,
													),
													deliveryPackageBundleId: packageBundle.deliveryPackageId!,
												});
												await Promise.all([refreshRef.refresh(), refreshParent?.()]);
												await closeDialog();
											}}
										/>
									),
								}));
							}}
						/>
					)}
				</>
			}
			rowContextMenuExtraComponents={({ allSelectedRows }) => [
				<AavoMenuDivider key={"dividerCustom"} />,
				editDeliveryPackagesPermission && packageBundle.deliveryId == null && (
					<AsyncMenuButton
						key={"remove_from_bundle"}
						label={i18n.t("delivery.remove_from_bundle")}
						icon={faMinus}
						onClick={async () => {
							await DeliveryPackageApi.removePackagesFromBundle({
								deliveryPackageIds: allSelectedRows.map((p) => p.deliveryPackageId),
							});
							await Promise.all([refreshRef.refresh(), refreshParent?.()]);
						}}
					/>
				),
			]}
		/>
	);
};
